import { Suspense, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { Trans, useTranslation } from 'react-i18next';
import type { Module } from '@pc/core';
import { useQuery } from '@tanstack/react-query';
import {
  listOrders,
  listShipments,
  listUpcomingAppointments,
  supabase,
  type ActiveContext,
} from '@pc/db';
import { Card, SmallCap } from '@pc/ui';
import { PartnerPairingBanner } from './PartnerPairingBanner';
import { useActiveContext } from '../auth/ActiveContextProvider';

interface DashboardProps {
  modules: ReadonlyArray<Module>;
}

export function Dashboard({ modules }: DashboardProps) {
  const { t } = useTranslation();
  const { activeOrgId, activeStoreId } = useActiveContext();
  const ctx = useMemo<ActiveContext | null>(() => {
    if (!activeOrgId || !activeStoreId) return null;
    return {
      orgId: activeOrgId,
      storeId: activeStoreId,
    };
  }, [activeOrgId, activeStoreId]);

  const hasOrders = modules.some((m) => m.id === 'orders');
  const hasFulfillment = modules.some((m) => m.id === 'fulfillment');
  const hasCalendar = modules.some((m) => m.id === 'calendar');

  const overdueAccountsQ = useQuery({
    queryKey: ['dashboard', 'overdue-accounts', ctx?.orgId, ctx?.storeId],
    enabled: !!ctx && hasOrders,
    queryFn: async () => {
      const nowIso = new Date().toISOString();
      const storeId = ctx!.storeId as string;
      // `invoiced`/`partially_paid` are the only states that should still
      // carry a collectible balance in normal operation.
      const { count, error } = await supabase()
        .schema('orders')
        .from('orders')
        .select('id', { count: 'exact', head: true })
        .eq('org_id', ctx!.orgId)
        .eq('store_id', storeId)
        .in('status', ['invoiced', 'partially_paid'])
        .lt('due_at', nowIso);
      if (error) throw error;
      return count ?? 0;
    },
    staleTime: 30_000,
  });

  const openOrdersQ = useQuery({
    queryKey: ['dashboard', 'open-orders', ctx?.orgId, ctx?.storeId],
    enabled: !!ctx && hasOrders,
    queryFn: async () => {
      const { total } = await listOrders(ctx!, {
        status: ['proposal_sent', 'approved', 'invoiced', 'partially_paid'],
        countMode: 'exact',
        pageSize: 1,
      });
      return total ?? 0;
    },
    staleTime: 30_000,
  });

  const pendingShipmentsQ = useQuery({
    queryKey: ['dashboard', 'pending-shipments', ctx?.orgId, ctx?.storeId],
    enabled: !!ctx && hasFulfillment,
    queryFn: async () => {
      const { total } = await listShipments(ctx!, {
        status: ['needs_label', 'ready_to_pack', 'packed', 'on_hold', 'exception'],
        countMode: 'exact',
        pageSize: 1,
      });
      return total ?? 0;
    },
    staleTime: 30_000,
  });

  const todayAppointmentsQ = useQuery({
    queryKey: ['dashboard', 'appointments-today', ctx?.orgId, ctx?.storeId],
    enabled: !!ctx && hasCalendar,
    queryFn: async () => {
      const rows = await listUpcomingAppointments(ctx!, 24);
      const now = new Date();
      const end = new Date(now);
      end.setHours(23, 59, 59, 999);
      return rows.filter((row) => {
        const startsAt = new Date(row.starts_at);
        return startsAt >= now && startsAt <= end;
      }).length;
    },
    staleTime: 30_000,
  });

  const widgets = useMemo(
    () =>
      modules
        .flatMap((m) =>
          (m.dashboardWidgets ?? []).map((w) => ({ ...w, moduleId: m.id, moduleName: m.name })),
        )
        .sort((a, b) => (a.order ?? 100) - (b.order ?? 100)),
    [modules],
  );
  const featuredWidgetIds = new Set([
    'orders-pipeline',
    'calendar-upcoming',
    'recent-clients',
  ]);
  const featuredWidgets = widgets.filter((w) => featuredWidgetIds.has(w.id));
  const otherWidgets = widgets.filter((w) => !featuredWidgetIds.has(w.id));
  const alertCards = [
    hasOrders
      ? {
          label: t('app.dashboard.alerts.overdueAccounts'),
          value: overdueAccountsQ.data ?? 0,
          tone: 'danger' as const,
          loading: overdueAccountsQ.isLoading,
          href: '/orders',
        }
      : null,
    hasFulfillment
      ? {
          label: t('app.dashboard.alerts.pendingShipments'),
          value: pendingShipmentsQ.data ?? 0,
          tone: 'warning' as const,
          loading: pendingShipmentsQ.isLoading,
          href: '/fulfillment',
        }
      : null,
    hasOrders
      ? {
          label: t('app.dashboard.alerts.openOrders'),
          value: openOrdersQ.data ?? 0,
          tone: 'neutral' as const,
          loading: openOrdersQ.isLoading,
          href: '/orders',
        }
      : null,
    hasCalendar
      ? {
          label: t('app.dashboard.alerts.appointmentsToday'),
          value: todayAppointmentsQ.data ?? 0,
          tone: 'neutral' as const,
          loading: todayAppointmentsQ.isLoading,
          href: '/calendar',
        }
      : null,
  ].filter((item): item is NonNullable<typeof item> => item !== null);
  const actionItems = [
    hasOrders
      ? {
          title: t('app.dashboard.queue.collectOverdue.title'),
          detail: t('app.dashboard.queue.collectOverdue.detail'),
          href: '/orders',
        }
      : null,
    hasFulfillment
      ? {
          title: t('app.dashboard.queue.pendingShipments.title'),
          detail: t('app.dashboard.queue.pendingShipments.detail'),
          href: '/fulfillment',
        }
      : null,
    hasCalendar
      ? {
          title: t('app.dashboard.queue.scheduleGaps.title'),
          detail: t('app.dashboard.queue.scheduleGaps.detail'),
          href: '/calendar',
        }
      : null,
  ].filter((item): item is NonNullable<typeof item> => item !== null);

  if (widgets.length === 0) {
    return (
      <>
        <PartnerPairingBanner />
        <Card
          title={
            <Trans
              i18nKey="app.dashboard.empty.title"
              components={{ em: <em /> }}
            />
          }
          hint={t('app.dashboard.empty.hint')}
        >
          <p className="text-sm text-ink-2">
            <Trans
              i18nKey="app.dashboard.empty.body"
              components={{ code: <code className="font-mono text-xs" /> }}
            />
          </p>
        </Card>
      </>
    );
  }

  return (
    <>
      <PartnerPairingBanner />
      <header className="mb-6">
        <SmallCap>{t('app.dashboard.eyebrow')}</SmallCap>
        <h1 className="display text-2xl md:text-3xl mt-1">
          {t('app.dashboard.title')}
        </h1>
        <p className="text-sm text-ink-2 mt-2 max-w-3xl">
          {t('app.dashboard.subtitle')}
        </p>
      </header>

      {alertCards.length > 0 ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-3 mb-6">
          {alertCards.map((card) => (
            <AlertStatCard
              key={card.label}
              label={card.label}
              value={card.value}
              tone={card.tone}
              loading={card.loading}
              href={card.href}
            />
          ))}
        </div>
      ) : null}

      {actionItems.length > 0 ? (
        <Card
          title={<>{t('app.dashboard.queue.title')}</>}
          hint={t('app.dashboard.queue.hint')}
          className="mb-6"
        >
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-sm">
            {actionItems.map((item) => (
              <ActionQueueItem
                key={item.title}
                title={item.title}
                detail={item.detail}
                href={item.href}
              />
            ))}
          </div>
        </Card>
      ) : null}

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {featuredWidgets.map((w) => {
          const Render = w.render;
          return (
            <div
              key={`${w.moduleId}:${w.id}`}
              className={
                w.span === 3
                  ? 'md:col-span-3'
                  : w.span === 2
                    ? 'md:col-span-2'
                    : 'md:col-span-1'
              }
            >
              <Card
                title={w.moduleName}
                hint={<SmallCap>{w.moduleId}</SmallCap>}
                className="h-full"
              >
                {/* Per-widget Suspense so one slow lazy() chunk degrades
                    gracefully instead of blanking the dashboard. Also keeps
                    the boundary adjacent to the suspending component, which
                    is what prevents React error #426 on initial commit. */}
                <Suspense fallback={<SmallCap>{t('actions.loading')}</SmallCap>}>
                  <Render />
                </Suspense>
              </Card>
            </div>
          );
        })}
      </div>

      {otherWidgets.length > 0 ? (
        <div className="mt-6">
          <SmallCap>{t('app.dashboard.otherSection')}</SmallCap>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-2">
            {otherWidgets.map((w) => {
              const Render = w.render;
              return (
                <div
                  key={`${w.moduleId}:${w.id}`}
                  className={
                    w.span === 3
                      ? 'md:col-span-3'
                      : w.span === 2
                        ? 'md:col-span-2'
                        : 'md:col-span-1'
                  }
                >
                  <Card
                    title={w.moduleName}
                    hint={<SmallCap>{w.moduleId}</SmallCap>}
                    className="h-full"
                  >
                    <Suspense fallback={<SmallCap>{t('actions.loading')}</SmallCap>}>
                      <Render />
                    </Suspense>
                  </Card>
                </div>
              );
            })}
          </div>
        </div>
      ) : null}
    </>
  );
}

function AlertStatCard({
  label,
  value,
  tone,
  loading,
  href,
}: {
  label: string;
  value: number;
  tone: 'danger' | 'warning' | 'neutral';
  loading: boolean;
  href: string;
}) {
  const valueClass =
    tone === 'danger'
      ? 'text-clay'
      : tone === 'warning'
        ? 'text-amber-700'
        : 'text-ink';
  return (
    <Link
      to={href}
      className="border border-rule bg-paper-2/20 px-4 py-3 hover:bg-paper-2/40 transition-colors"
    >
      <p className="text-[11px] uppercase tracking-wider text-muted">{label}</p>
      <p className={`mt-1 text-2xl display ${valueClass}`}>
        {loading ? '...' : value.toLocaleString()}
      </p>
    </Link>
  );
}

function ActionQueueItem({
  title,
  detail,
  href,
}: {
  title: string;
  detail: string;
  href: string;
}) {
  return (
    <Link
      to={href}
      className="border border-rule bg-paper-2/20 p-3 hover:bg-paper-2/40 transition-colors"
    >
      <p className="text-sm text-ink">{title}</p>
      <p className="text-xs text-muted mt-1">{detail}</p>
    </Link>
  );
}
